import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { authGet, authPost, authDelete } from '../../services/apiService';

const SnykMapping = () => {
  const [mappings, setMappings] = useState([]);
  const [unmappedProjectTargets, setUnmappedProjectTargets] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [assigning, setAssigning] = useState({});
  const [selectedTeams, setSelectedTeams] = useState({});
  const [newRepoDialogOpen, setNewRepoDialogOpen] = useState(false);
  const [newRepoForm, setNewRepoForm] = useState({
    Repo: '',
    Team: '',
    EM: '',
    Owner: '',
    Stream: '',
    reviewers: '',
    drill4JAppId: '',
    type: 'modern',
    sonarProjectName: ''
  });
  const [savingRepo, setSavingRepo] = useState(false);

  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [mappingsResponse, unmappedResponse, teamsResponse] = await Promise.all([
        authGet('/api/snyk-mapping/mappings'),
        authGet('/api/snyk-mapping/unmapped-project-targets'),
        authGet('/api/snyk-mapping/teams')
      ]);

      setMappings(mappingsResponse.mappings || []);
      setUnmappedProjectTargets(unmappedResponse.unmapped || []);
      setTeams(teamsResponse.teams || []);

    } catch (err) {
      console.error('Error fetching Repo mapping data:', err);
      setError(err.response?.data?.error || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle JSON file upload
  const handleJsonUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setError('Please upload a JSON file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const fileContent = await file.text();
      const jsonData = JSON.parse(fileContent);

      if (!Array.isArray(jsonData)) {
        setError('JSON file must contain an array of objects');
        return;
      }

      const response = await authPost('/api/snyk-mapping/upload-json', {
        data: jsonData,
        clearExisting: true // Always clear existing records before importing
      });

      if (response.success) {
        setSuccess(
          `Successfully imported ${response.summary.success} mappings. ` +
          `Modified: ${response.summary.modifiedCount}, New: ${response.summary.upsertedCount}`
        );
        if (response.errors && response.errors.length > 0) {
          console.warn('Import warnings:', response.errors);
        }
        fetchData(); // Refresh data
      } else {
        setError(response.error || 'Failed to import mappings');
      }
    } catch (err) {
      console.error('Error uploading JSON:', err);
      if (err instanceof SyntaxError) {
        setError('Invalid JSON format. Please check your file.');
      } else {
        setError(err.response?.data?.error || 'Failed to upload JSON file');
      }
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Handle delete mapping
  const handleDelete = async (mappingId) => {
    // Find the mapping to get Project info for user feedback
    const mappingToDelete = mappings.find(m => m._id === mappingId);
    const projectName = mappingToDelete?.Project || mappingToDelete?.Repo || 'the mapping';
    
    if (!window.confirm(`Are you sure you want to delete this mapping for "${projectName}"? This will add it to the unmapped project targets list.`)) {
      return;
    }

    try {
      setError(null);
      await authDelete(`/api/snyk-mapping/mappings/${mappingId}`);
      setSuccess(`Mapping deleted successfully. "${projectName}" will appear in unmapped project targets.`);
      // Refresh data to show updated mappings and unmapped list
      await fetchData();
    } catch (err) {
      console.error('Error deleting mapping:', err);
      setError(err.response?.data?.error || 'Failed to delete mapping');
    }
  };

  // Handle download JSON
  const handleDownloadJson = () => {
    try {
      // Format mappings for export - include ALL fields including repository properties
      // Always include all properties even if empty, so users can see and edit them
      const exportData = mappings.map(mapping => {
        const exportItem = {
          Project: mapping.Project || '',
          Repo: mapping.Repo || '',
          Team: mapping.Team || '',
          EM: mapping.EM || '',
          Owner: mapping.Owner || '',
          Stream: mapping.Stream || '',
          // Always include repository properties (even if empty)
          drill4JAppId: mapping.drill4JAppId || '',
          type: mapping.type || 'modern',
          sonarProjectName: mapping.sonarProjectName || '',
          syncStatus: mapping.syncStatus || 'not_synced',
          lastSyncAt: mapping.lastSyncAt || null
        };
        
        // Include ignore field if it's true or exists
        if (mapping.ignore === true || mapping.ignore === 'true') {
          exportItem.ignore = true;
        }
        
        // Always include nested objects (even if empty) so structure is consistent
        exportItem.githubData = mapping.githubData || {};
        exportItem.sonarData = mapping.sonarData || {};
        
        // Include reviewers array if it exists
        if (mapping.reviewers && Array.isArray(mapping.reviewers) && mapping.reviewers.length > 0) {
          exportItem.reviewers = mapping.reviewers;
        }
        
        return exportItem;
      });

      // Create JSON blob
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `snyk-mappings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSuccess('Mappings downloaded successfully');
    } catch (err) {
      console.error('Error downloading JSON:', err);
      setError('Failed to download mappings JSON');
    }
  };

  // Handle assign project target to team
  const handleAssignProjectTarget = async (projectTarget, team) => {
    try {
      setError(null);
      setAssigning(prev => ({ ...prev, [projectTarget.original]: true }));

      const response = await authPost('/api/snyk-mapping/assign-project-target', {
        projectTarget: projectTarget.original,
        team: team
      });

      if (response.success) {
        setSuccess(`Successfully assigned "${projectTarget.display}" to ${team}`);
        // Clear the selected team for this project target
        setSelectedTeams(prev => {
          const newState = { ...prev };
          delete newState[projectTarget.original];
          return newState;
        });
        // Refresh data to update unmapped list and mappings
        await fetchData();
      } else {
        setError(response.error || 'Failed to assign project target');
      }
    } catch (err) {
      console.error('Error assigning project target:', err);
      setError(err.response?.data?.error || 'Failed to assign project target');
    } finally {
      setAssigning(prev => ({ ...prev, [projectTarget.original]: false }));
    }
  };

  // Clear messages
  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // Handle new repository form submission
  const handleNewRepoSubmit = async () => {
    if (!newRepoForm.Repo || !newRepoForm.Team) {
      setError('Repo and Team are required fields');
      return;
    }

    try {
      setSavingRepo(true);
      setError(null);
      setSuccess(null);

      // Parse reviewers from comma-separated string
      const reviewers = newRepoForm.reviewers
        ? newRepoForm.reviewers.split(',').map(r => r.trim()).filter(r => r)
        : [];

      const response = await authPost('/api/snyk-mapping/mappings', {
        repository: newRepoForm.Repo,
        team: newRepoForm.Team,
        em: newRepoForm.EM || '',
        owner: newRepoForm.Owner || '',
        stream: newRepoForm.Stream || '',
        reviewers: reviewers,
        drill4JAppId: newRepoForm.drill4JAppId || '',
        type: newRepoForm.type || 'modern',
        sonarProjectName: newRepoForm.sonarProjectName || ''
      });

      if (response.success) {
        setSuccess('Repository created successfully');
        setNewRepoDialogOpen(false);
        setNewRepoForm({
          Repo: '',
          Team: '',
          EM: '',
          Owner: '',
          Stream: '',
          reviewers: '',
          drill4JAppId: '',
          type: 'modern',
          sonarProjectName: ''
        });
        await fetchData();
      } else {
        setError(response.error || 'Failed to create repository');
      }
    } catch (err) {
      console.error('Error creating repository:', err);
      setError(err.response?.data?.error || 'Failed to create repository');
    } finally {
      setSavingRepo(false);
    }
  };

  // Handle new repository dialog close
  const handleNewRepoDialogClose = () => {
    setNewRepoDialogOpen(false);
    setNewRepoForm({
      Repo: '',
      Team: '',
      EM: '',
      Owner: '',
      Stream: '',
      reviewers: '',
      drill4JAppId: '',
      type: 'modern',
      sonarProjectName: ''
    });
  };

  // Group mappings by Stream only
  const groupedByStream = useMemo(() => {
    const grouped = {};
    mappings.forEach((mapping) => {
      const stream = mapping.Stream || 'Unassigned Stream';
      
      if (!grouped[stream]) {
        grouped[stream] = [];
      }
      grouped[stream].push(mapping);
    });
    
    // Sort streams alphabetically, and sort repositories within each stream
    const sortedGroups = {};
    Object.keys(grouped).sort().forEach(stream => {
      sortedGroups[stream] = grouped[stream].sort((a, b) => {
        const repoA = (a.Repo || a.repository || '').toLowerCase();
        const repoB = (b.Repo || b.repository || '').toLowerCase();
        return repoA.localeCompare(repoB);
      });
      // Assign sequential numbers starting from 1 within each stream
      sortedGroups[stream] = sortedGroups[stream].map((mapping, index) => ({
        ...mapping,
        streamIndex: index + 1
      }));
    });
    
    return sortedGroups;
  }, [mappings]);

  if (loading && mappings.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Repo Mapping
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearMessages}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={clearMessages}>
          {success}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2} flexWrap="wrap">
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setNewRepoDialogOpen(true)}
        >
          New Repository
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<UploadIcon />}
          component="label"
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload JSON'}
          <input
            type="file"
            accept=".json"
            hidden
            onChange={handleJsonUpload}
            disabled={uploading}
          />
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadJson}
          disabled={mappings.length === 0}
        >
          Download JSON
        </Button>
      </Box>

      {/* Unmapped Project Targets Section */}
      {unmappedProjectTargets.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'warning.main' }}>
              Unmapped Project Targets ({unmappedProjectTargets.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These project targets exist in Snyk issues but don't have team mappings. Assign them to a team below.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Project Target</strong></TableCell>
                    <TableCell><strong>Assign to Team</strong></TableCell>
                    <TableCell><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unmappedProjectTargets.map((projectTarget) => (
                    <TableRow key={projectTarget.original} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {projectTarget.display}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Team</InputLabel>
                          <Select
                            value={selectedTeams[projectTarget.original] || ''}
                            label="Select Team"
                            onChange={(e) => {
                              const selectedTeam = e.target.value;
                              setSelectedTeams(prev => ({
                                ...prev,
                                [projectTarget.original]: selectedTeam
                              }));
                              if (selectedTeam) {
                                handleAssignProjectTarget(projectTarget, selectedTeam);
                              }
                            }}
                            disabled={assigning[projectTarget.original]}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {teams.map((team) => (
                              <MenuItem key={team.id} value={team.name}>
                                {team.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        {assigning[projectTarget.original] && (
                          <CircularProgress size={20} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Mappings Tree View */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Repository Mappings (Grouped by Stream)
          </Typography>
          
          {mappings.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No mappings found. Upload a JSON file to create mappings.
              </Typography>
            </Box>
          ) : (
            <Box>
              {Object.entries(groupedByStream).map(([stream, streamMappings]) => (
                <Accordion key={stream} defaultExpanded sx={{ mb: 2 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.12)'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" width="100%">
                      <Chip
                        label={stream}
                        color="primary"
                        size="medium"
                        sx={{ mr: 2, fontWeight: 600 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        ({streamMappings.length} {streamMappings.length === 1 ? 'repository' : 'repositories'})
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>#</strong></TableCell>
                            <TableCell><strong>Project</strong></TableCell>
                            <TableCell><strong>Repo</strong></TableCell>
                            <TableCell><strong>Team</strong></TableCell>
                            <TableCell><strong>EM</strong></TableCell>
                            <TableCell><strong>Stream</strong></TableCell>
                            <TableCell><strong>Reviewers</strong></TableCell>
                            <TableCell><strong>drill4JAppId</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {streamMappings.map((mapping) => (
                            <TableRow key={mapping._id} hover>
                              <TableCell>
                                <Typography variant="body2">
                                  {mapping.streamIndex}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {mapping.Project || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {mapping.Repo || mapping.repository || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={mapping.Team || '-'}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {mapping.EM || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {mapping.Stream || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {mapping.reviewers && Array.isArray(mapping.reviewers) && mapping.reviewers.length > 0
                                    ? mapping.reviewers.join(', ')
                                    : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ 
                                  fontFamily: 'monospace',
                                  color: mapping.drill4JAppId ? 'text.primary' : 'text.disabled'
                                }}>
                                  {mapping.drill4JAppId || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(mapping._id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* New Repository Dialog */}
      <Dialog
        open={newRepoDialogOpen}
        onClose={handleNewRepoDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Repository</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Repo URL *"
                value={newRepoForm.Repo}
                onChange={(e) => setNewRepoForm({ ...newRepoForm, Repo: e.target.value })}
                required
                helperText="Repository URL or name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Team *</InputLabel>
                <Select
                  value={newRepoForm.Team}
                  label="Team *"
                  onChange={(e) => setNewRepoForm({ ...newRepoForm, Team: e.target.value })}
                >
                  {teams.map((team) => (
                    <MenuItem key={team.id} value={team.name}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="EM"
                value={newRepoForm.EM}
                onChange={(e) => setNewRepoForm({ ...newRepoForm, EM: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Owner"
                value={newRepoForm.Owner}
                onChange={(e) => setNewRepoForm({ ...newRepoForm, Owner: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stream"
                value={newRepoForm.Stream}
                onChange={(e) => setNewRepoForm({ ...newRepoForm, Stream: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newRepoForm.type}
                  label="Type"
                  onChange={(e) => setNewRepoForm({ ...newRepoForm, type: e.target.value })}
                >
                  <MenuItem value="modern">Modern</MenuItem>
                  <MenuItem value="legacy">Legacy</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sonar Project Name"
                value={newRepoForm.sonarProjectName}
                onChange={(e) => setNewRepoForm({ ...newRepoForm, sonarProjectName: e.target.value })}
                helperText="Leave empty to use repo name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Drill4J App ID"
                value={newRepoForm.drill4JAppId}
                onChange={(e) => setNewRepoForm({ ...newRepoForm, drill4JAppId: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reviewers"
                value={newRepoForm.reviewers}
                onChange={(e) => setNewRepoForm({ ...newRepoForm, reviewers: e.target.value })}
                helperText="Comma-separated list of reviewers"
                placeholder="reviewer1, reviewer2"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNewRepoDialogClose} disabled={savingRepo}>
            Cancel
          </Button>
          <Button
            onClick={handleNewRepoSubmit}
            variant="contained"
            color="primary"
            disabled={savingRepo || !newRepoForm.Repo || !newRepoForm.Team}
          >
            {savingRepo ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SnykMapping;
